/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import * as Yup from 'yup'
import clsx from 'clsx'
import { useFormik } from 'formik'
import * as auth from '../redux/AuthRedux'
import { login } from '../redux/AuthCRUD'
import { useLoading } from '../../../core/LoadingProvider'

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Geçersiz e-mail formatı')
    .min(3, 'Minimum 3 karakter')
    .max(50, 'Maximum 50 karakter')
    .required('E-mail zorunludur'),
  password: Yup.string()
    .min(3, 'Minimum 3 karakter')
    .max(50, 'Maximum 50 karakter')
    .required('Şifre zorunludur'),
})

const initialValues = {
  email: '',
  password: '',
  rememberMe: false,
}

export function Login2() {
  const { loading, setLoading } = useLoading()
  const [showPassword, setShowPassword] = useState(false)
  const dispatch = useDispatch()

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      login(values.email, values.password)
        .then(({ data: { accessToken, refreshToken, corporateId } }) => {
          setLoading(false)
          dispatch(auth.actions.login(accessToken, refreshToken, corporateId))
        })
        .catch(() => {
          setLoading(false)
          setSubmitting(false)
          setStatus('E-mail veya şifre hatalı. Lütfen tekrar deneyin.')
        })
    },
  })

  return (
    <>
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes floatCard {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-6px); }
        }
        @keyframes pulseRing {
          0%   { transform: scale(0.9); opacity: 0.6; }
          100% { transform: scale(1.4); opacity: 0; }
        }
        @keyframes shimmer {
          0%   { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }

        .login2-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
          position: relative;
          overflow: hidden;
          font-family: 'Inter', sans-serif;
        }

        /* Animated background blobs */
        .login2-blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.25;
          pointer-events: none;
        }
        .login2-blob-1 {
          width: 500px; height: 500px;
          background: radial-gradient(circle, #6366f1, transparent);
          top: -150px; left: -150px;
          animation: floatCard 7s ease-in-out infinite;
        }
        .login2-blob-2 {
          width: 400px; height: 400px;
          background: radial-gradient(circle, #a855f7, transparent);
          bottom: -100px; right: -100px;
          animation: floatCard 9s ease-in-out infinite reverse;
        }
        .login2-blob-3 {
          width: 300px; height: 300px;
          background: radial-gradient(circle, #06b6d4, transparent);
          top: 40%; left: 60%;
          animation: floatCard 11s ease-in-out infinite;
        }

        /* Grid dots */
        .login2-grid {
          position: absolute;
          inset: 0;
          background-image: radial-gradient(circle, rgba(255,255,255,0.07) 1px, transparent 1px);
          background-size: 40px 40px;
          pointer-events: none;
        }

        /* Card */
        .login2-card {
          position: relative;
          z-index: 10;
          width: 100%;
          max-width: 460px;
          margin: 24px;
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 24px;
          padding: 48px 44px;
          animation: fadeInUp 0.6s ease both;
          box-shadow:
            0 32px 64px rgba(0, 0, 0, 0.4),
            0 0 0 1px rgba(255,255,255,0.06) inset,
            0 1px 0 rgba(255,255,255,0.15) inset;
        }

        /* Logo area */
        .login2-logo-wrap {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 36px;
          position: relative;
        }
        .login2-logo-ring {
          position: absolute;
          width: 80px; height: 80px;
          border-radius: 50%;
          border: 2px solid rgba(99,102,241,0.5);
          animation: pulseRing 2s ease-out infinite;
        }
        .login2-logo-img {
          height: 52px;
          position: relative;
          z-index: 1;
          filter: drop-shadow(0 0 16px rgba(99,102,241,0.6));
        }

        /* Heading */
        .login2-heading {
          text-align: center;
          margin-bottom: 32px;
        }
        .login2-title {
          font-size: 26px;
          font-weight: 700;
          color: #ffffff;
          margin: 0 0 6px 0;
          letter-spacing: -0.5px;
        }
        .login2-subtitle {
          font-size: 14px;
          color: rgba(255,255,255,0.45);
          margin: 0;
        }

        /* Alert */
        .login2-alert {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          background: rgba(239, 68, 68, 0.15);
          border: 1px solid rgba(239,68,68,0.35);
          border-radius: 12px;
          padding: 12px 14px;
          margin-bottom: 22px;
          color: #fca5a5;
          font-size: 13.5px;
          animation: fadeInUp 0.3s ease both;
        }
        .login2-alert-icon { font-size: 16px; margin-top: 1px; flex-shrink: 0; }

        /* Form group */
        .login2-group { margin-bottom: 20px; }
        .login2-label {
          display: block;
          font-size: 13px;
          font-weight: 500;
          color: rgba(255,255,255,0.65);
          margin-bottom: 8px;
          letter-spacing: 0.3px;
        }
        .login2-input-wrap {
          position: relative;
          display: flex;
          align-items: center;
        }
        .login2-input-icon {
          position: absolute;
          left: 14px;
          color: rgba(255,255,255,0.3);
          font-size: 16px;
          pointer-events: none;
          transition: color 0.2s;
        }
        .login2-input {
          width: 100%;
          height: 50px;
          background: rgba(255,255,255,0.06);
          border: 1.5px solid rgba(255,255,255,0.1);
          border-radius: 12px;
          color: #ffffff;
          font-size: 14.5px;
          padding: 0 44px 0 42px;
          outline: none;
          transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
        }
        .login2-input::placeholder { color: rgba(255,255,255,0.22); }
        .login2-input:focus {
          border-color: #6366f1;
          background: rgba(99,102,241,0.08);
          box-shadow: 0 0 0 4px rgba(99,102,241,0.15);
        }
        .login2-input.is-invalid { border-color: #ef4444 !important; }
        .login2-input.is-valid   { border-color: #22c55e !important; }
        .login2-eye-btn {
          position: absolute;
          right: 14px;
          background: none;
          border: none;
          padding: 4px;
          cursor: pointer;
          color: rgba(255,255,255,0.3);
          font-size: 16px;
          transition: color 0.2s;
          line-height: 1;
        }
        .login2-eye-btn:hover { color: rgba(255,255,255,0.7); }
        .login2-error {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 12px;
          color: #f87171;
          margin-top: 6px;
          padding-left: 2px;
        }

        /* Remember row */
        .login2-remember-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 28px;
        }
        .login2-check-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          font-size: 13.5px;
          color: rgba(255,255,255,0.55);
          user-select: none;
        }
        .login2-check-input {
          width: 16px; height: 16px;
          accent-color: #6366f1;
          cursor: pointer;
          border-radius: 4px;
        }

        /* Submit button */
        .login2-btn {
          width: 100%;
          height: 52px;
          border: none;
          border-radius: 14px;
          font-size: 15px;
          font-weight: 600;
          color: #ffffff;
          cursor: pointer;
          position: relative;
          overflow: hidden;
          transition: transform 0.15s, box-shadow 0.15s;
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%);
          box-shadow: 0 8px 24px rgba(99,102,241,0.45);
          letter-spacing: 0.3px;
        }
        .login2-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent);
          background-size: 400px 100%;
          animation: shimmer 2.5s linear infinite;
        }
        .login2-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 12px 32px rgba(99,102,241,0.55);
        }
        .login2-btn:active:not(:disabled) { transform: translateY(0); }
        .login2-btn:disabled {
          opacity: 0.65;
          cursor: not-allowed;
          transform: none;
        }
        .login2-btn-inner {
          position: relative;
          z-index: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        /* Spinner */
        .login2-spinner {
          width: 18px; height: 18px;
          border: 2.5px solid rgba(255,255,255,0.35);
          border-top-color: #ffffff;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* Divider */
        .login2-divider {
          display: flex;
          align-items: center;
          gap: 12px;
          margin: 24px 0;
        }
        .login2-divider-line {
          flex: 1;
          height: 1px;
          background: rgba(255,255,255,0.1);
        }
        .login2-divider-text {
          font-size: 12px;
          color: rgba(255,255,255,0.3);
          white-space: nowrap;
        }

        /* Footer links */
        .login2-footer {
          text-align: center;
          margin-top: 24px;
        }
        .login2-footer-text {
          font-size: 12.5px;
          color: rgba(255,255,255,0.3);
        }

        /* Input focus — sibling icon color */
        .login2-input-wrap:focus-within .login2-input-icon {
          color: #818cf8;
        }
      `}</style>

      <div className='login2-page'>
        {/* Background decorations */}
        <div className='login2-grid' />
        <div className='login2-blob login2-blob-1' />
        <div className='login2-blob login2-blob-2' />
        <div className='login2-blob login2-blob-3' />

        <div className='login2-card'>
          {/* Logo */}
          <div className='login2-logo-wrap'>
            <div className='login2-logo-ring' />
            <img
              src='/media/logos/eywa-logo.png'
              alt='Eywa Logo'
              className='login2-logo-img'
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none'
              }}
            />
          </div>

          {/* Heading */}
          <div className='login2-heading'>
            <h1 className='login2-title'>Dijital Rent</h1>
            <p className='login2-subtitle'>Hesabınıza giriş yapın</p>
          </div>

          {/* Error */}
          {formik.status && (
            <div className='login2-alert'>
              <span className='login2-alert-icon'>⚠</span>
              <span>{formik.status}</span>
            </div>
          )}

          <form onSubmit={formik.handleSubmit} noValidate>
            {/* Email */}
            <div className='login2-group'>
              <label className='login2-label'>E-posta Adresi</label>
              <div className='login2-input-wrap'>
                <span className='login2-input-icon'>✉</span>
                <input
                  type='email'
                  placeholder='ornek@sirket.com'
                  autoComplete='email'
                  {...formik.getFieldProps('email')}
                  className={clsx('login2-input', {
                    'is-invalid': formik.touched.email && formik.errors.email,
                    'is-valid': formik.touched.email && !formik.errors.email,
                  })}
                />
              </div>
              {formik.touched.email && formik.errors.email && (
                <div className='login2-error'>
                  <span>⚑</span>
                  <span>{formik.errors.email}</span>
                </div>
              )}
            </div>

            {/* Password */}
            <div className='login2-group'>
              <label className='login2-label'>Şifre</label>
              <div className='login2-input-wrap'>
                <span className='login2-input-icon'>🔒</span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder='••••••••'
                  autoComplete='current-password'
                  {...formik.getFieldProps('password')}
                  className={clsx('login2-input', {
                    'is-invalid': formik.touched.password && formik.errors.password,
                    'is-valid': formik.touched.password && !formik.errors.password,
                  })}
                />
                <button
                  type='button'
                  className='login2-eye-btn'
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label='Şifreyi göster/gizle'
                >
                  {showPassword ? '🙈' : '👁'}
                </button>
              </div>
              {formik.touched.password && formik.errors.password && (
                <div className='login2-error'>
                  <span>⚑</span>
                  <span>{formik.errors.password}</span>
                </div>
              )}
            </div>

            {/* Remember Me */}
            <div className='login2-remember-row'>
              <label className='login2-check-label'>
                <input
                  type='checkbox'
                  className='login2-check-input'
                  {...formik.getFieldProps('rememberMe')}
                  checked={formik.values.rememberMe}
                />
                Beni hatırla
              </label>
            </div>

            {/* Submit */}
            <button
              type='submit'
              className='login2-btn'
              disabled={formik.isSubmitting || loading}
            >
              <span className='login2-btn-inner'>
                {loading ? (
                  <>
                    <span className='login2-spinner' />
                    <span>Giriş yapılıyor...</span>
                  </>
                ) : (
                  <>
                    <span>Giriş Yap</span>
                    <span>→</span>
                  </>
                )}
              </span>
            </button>
          </form>

          <div className='login2-divider'>
            <div className='login2-divider-line' />
            <span className='login2-divider-text'>Dijital Rent v2.0</span>
            <div className='login2-divider-line' />
          </div>

          <div className='login2-footer'>
            <span className='login2-footer-text'>
              © 2026 Dijital Rent · Tüm hakları saklıdır
            </span>
          </div>
        </div>
      </div>
    </>
  )
}
